import {React, observer, withStyles, StyledProps, classNames, _, lockr, Fragment, useAsObservableSource, classObserver} from '@api'
import {Avatar} from '@material-ui/core'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {computed, observable, reaction} from 'mobx'
import VisibilitySensor from 'react-visibility-sensor'
import * as css from './LazyAvatar.css'
import tinycolor from 'tinycolor2'
import {LinkOff} from '@material-ui/icons'
import {nav, StoresInstance} from '@stores'

const styles = () => ({
  root: {
    borderRadius: 2,
    backgroundColor: 'rgb(216, 216, 216)',
    color: '#FFF',
    fontSize: 20,
    fontWeight: 500
  },
  img: {},
  noImage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFF'
  }
})

interface MyProps extends StyledProps {
  src?: string,
  hex?: string,
  showHex?: boolean
  lazy?: boolean
  extra?: React.ReactNode
  imageId?: string
  children?: React.ReactNode
}

export class LazyAvatarStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Partial<MyProps>) {
    super(stores)
  }

  @observable imageLoadError = false

  onImageLoadError = (e) => {
    this.imageLoadError = true
    if (e.target?.src?.indexOf('TokenURL=centric') != -1) {
      console.warn(`Error loading C8 image - possible token expiration`)

      if (!lockr.get('SecurityTokenURL') && !DEBUG) {
        nav.routeToLogoutPage()
      }
    }
  }

  @computed get className() {
    const {props: {hex, className}} = this
    const color = tinycolor(hex)
    const isBright: Boolean = _.round(color.toHsv().v, 2) > 0.96 && _.round(color.getLuminance(), 2) > 0.96
    return classNames(css.root, {[css.border]: isBright}, className)
  }

  @computed get style() {
    const {hex, src, showHex} = this.props,
      {isRgba} = this

    return {
      backgroundColor: showHex && hex ? hex : src ? isRgba ? src : undefined : undefined
    }
  }

  @computed get isRgba() {
    return this.props.src?.startsWith('rgba(')
  }

  @computed get src() {
    return this.props.src
  }

  @computed get avatar() {
    const {
      props: {
        hex,
        classes,
        children,
        showHex,
        className: ignored,
        lazy,
        imageId,
        extra,
        ...other
      },
      className,
      isRgba,
      imageLoadError,
      onImageLoadError,
      style,
      src
    } = this

    return (
      <Fragment>
        {imageLoadError && (!imageId) ?
         <span className={classNames(className, classes.noImage)}
               data-src={src}
               data-type='LazyAvatar'>
           {extra}
           <LinkOff/>
    </span> : <Fragment>
           <Avatar
             {...other}
             onError={onImageLoadError}
             classes={{root: classes.root, img: classes.img}}
             src={!isRgba && src ? src : undefined}
             key={src}
             imgProps={{
               draggable: false
             }}
             data-type='LazyAvatar'
             data-src={src}
             data-no-image={_.isEmpty(src) || isRgba}
             className={className}
             style={style}
             >
             {children}
             {extra}
           </Avatar>
           {(src && !isRgba) && extra}
         </Fragment>
        }</Fragment>)
  }
}

const LazyAvatarInternal = observer<MyProps>(function LazyAvatar(props, ref) {
  const obsProps = useAsObservableSource(props),
    store = useVipLocalStore((ws) => new LazyAvatarStore(ws, obsProps)),
    {
      props: {
        hex,
        classes,
        children,
        showHex,
        lazy,
        className:
          ignored,
        ...
          other
      },
      avatar,
      className,
      src
    } = store

  return (
    !lazy ? avatar :
    <VisibilitySensor partialVisibility>
      {({isVisible}) =>
        isVisible
        ? avatar
        : <div className={className} {...other} />
      }
    </VisibilitySensor>
  )
}, {forwardRef: true})

export const LazyAvatar = withStyles(styles)(LazyAvatarInternal) as React.FunctionComponent<MyProps>

import IntersectionVisible from 'react-intersection-visible'
import {MyImageStore} from './QRCode/MyImage'

@(withStyles as any)(styles)
@classObserver
export class LazyAvatarIntersectionVisible extends React.Component<MyProps> {
  static defaultProps = {}

  @observable visible = false

  render() {
    let {
      visible,
      props: {
        hex,
        src,
        classes,
        children,
        showHex,
        className,
        ...other
      }
    } = this

    className = classNames(css.root, showHex && hex && hex === '#FFFFFF' && css.border, className)

    return (
      <IntersectionVisible onHide={e => this.visible = false} onShow={e => this.visible = true}>
        {visible
         ? <Avatar
           classes={{root: classes.root, img: classes.img}}
           src={src ? src : undefined}
           imgProps={{
             draggable: false
           }}
           className={className}
           style={{backgroundColor: showHex && hex ? hex : src ? 'transparent' : 'rgba(216, 216, 216)'}}
           {...other}>
           {children}
         </Avatar>
         : <div className={className} {...other} />
        }
      </IntersectionVisible>
    )
  }
}



// WEBPACK FOOTER //
// ./src/shared/components/ui/components/LazyAvatar.tsx